import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DECK } from '@/constants/testIds';
import PrisonLock from '@/components/PrisonLock';

export default function PreloaderTitleReveal({ onEnter }) {
  const [progress, setProgress] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let p = 0;
    const interval = setInterval(() => {
      p = Math.min(100, p + 6 + Math.random() * 8);
      setProgress(p);
      if (p >= 100) {
        clearInterval(interval);
        setTimeout(() => setReady(true), 250);
      }
    }, 90);
    return () => clearInterval(interval);
  }, []);

  const handleEnter = () => {
    if (onEnter) onEnter();
  };

  return (
    <div data-testid={DECK.preloader} className="preloader-wrap">
      {/* Atmospheric backplate — underwater Pandi (bullet wound, tangled in
          nets, fire above) sits behind the entire preloader at MINIMUM
          brightness, exactly as requested. Reads as a subliminal mood layer
          without competing with the BLACK AXE title, logline, or CTA. */}
      <div
        aria-hidden="true"
        data-testid="preloader-backplate"
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: "url('/assets/cold-open/first-page.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center 35%',
          backgroundRepeat: 'no-repeat',
          filter: 'brightness(0.6) saturate(0.95) contrast(1.05)',
          WebkitMaskImage:
            'radial-gradient(ellipse 95% 105% at 50% 45%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.95) 60%, rgba(0,0,0,0.55) 88%, rgba(0,0,0,0) 100%)',
          maskImage:
            'radial-gradient(ellipse 95% 105% at 50% 45%, rgba(0,0,0,1) 0%, rgba(0,0,0,0.95) 60%, rgba(0,0,0,0.55) 88%, rgba(0,0,0,0) 100%)',
          zIndex: 0,
        }}
      />
      {/* Tinted gradient bias toward the deck's oxblood/black palette so the
          gold title and red blood-text always have full contrast. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 60% 70% at 50% 50%, rgba(0,0,0,0.25), rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.78) 100%)',
          zIndex: 0,
        }}
      />
      <div className="absolute inset-0 bg-vignette pointer-events-none" />
      <div className="absolute inset-0 bg-noise pointer-events-none" />
      <div className="relative z-10 max-w-[820px] w-full px-6 text-center">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <div className="mb-6">
            <div className="font-display blood-text tracking-[0.34em] text-xs sm:text-sm">RAGHAVA LAWRENCE</div>
            <div className="mt-1 font-display tracking-[0.32em] text-[10px] sm:text-xs text-[color:var(--text-sand)]">IN AND AS</div>
          </div>
          <h1 className="title-poster leading-tight tracking-tight text-6xl sm:text-8xl md:text-[110px]">
            <motion.span initial={{ clipPath: 'inset(0 100% 0 0)' }} animate={{ clipPath: 'inset(0 0% 0 0)' }} transition={{ duration: 1.1, ease: [0.6, 0, 0.4, 1] }} className="block whitespace-nowrap">
              BLACK AXE
            </motion.span>
          </h1>
          <div className="mt-5 font-display tracking-[0.28em] text-[11px] sm:text-xs text-[color:var(--text-sand)]">
            WRITTEN AND DIRECTED BY <span className="text-[color:var(--gold)]">ANL ARASU</span>
          </div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.9, duration: 0.7 }} className="mt-6 font-display italic text-lg sm:text-xl text-[color:var(--text-cream)]">
            “He survived the bullet. Now the bullet is the weapon.”
          </motion.p>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.2, duration: 0.6 }} className="mt-10 mx-auto max-w-[420px]">
            <div className="flex justify-between mb-2 font-ui text-[10px] tracking-[0.28em] uppercase text-[color:var(--text-dim)]">
              <span>Initializing Hologram</span>
              <span>{Math.floor(progress)}%</span>
            </div>
            <div className="h-[2px] w-full bg-white/10 rounded">
              <motion.div className="h-full rounded" style={{ background: 'linear-gradient(90deg, #C8A24A, #F2D27A, #C8A24A)' }} animate={{ width: `${progress}%` }} transition={{ ease: 'easeOut', duration: 0.25 }} />
            </div>
            <div className="mt-6 flex justify-center">
              <span className="tick" />
              <span className="tick" />
              <span className="tick" />
              <span className="tick" />
            </div>
          </motion.div>
          <AnimatePresence>
            {ready && (
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.6 }}
                className="mt-8 flex flex-col items-center"
              >
                <PrisonLock onUnlock={handleEnter} />
                {/* Hidden testID-bearing entry button so existing tests / a11y
                    tooling that look for `enter-deck-button` still pass. The
                    real entry is now driven by the lock above. */}
                <button
                  data-testid={DECK.enterButton}
                  type="button"
                  className="sr-only"
                  onClick={handleEnter}
                  aria-hidden="true"
                  tabIndex={-1}
                >
                  Enter the Deck
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          <div className="mt-12 font-ui text-[10px] tracking-[0.32em] uppercase text-[color:var(--text-dim)]">
            COMING SOON
          </div>
        </motion.div>
      </div>
    </div>
  );
}
